import React, { useEffect, useMemo, useRef, useState } from "react";
import CommitOnBlurNumberInput from "../CommitOnBlurNumberInput";
import { ChromePicker } from "react-color";
import { FONT_OPTIONS, getSelectedFontValue, getFontSizeFromStyle } from "./StylePanel.js";
import { preloadMappedSdfFontsForText } from "../../../../../../engine/modules/ui/labelFontOptions.js";
import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import { OnChangePlugin } from "@lexical/react/LexicalOnChangePlugin";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { LexicalErrorBoundary } from "@lexical/react/LexicalErrorBoundary";
import {
  $createParagraphNode,
  $createTextNode,
  $getRoot,
  $getSelection,
  $isElementNode,
  $isRangeSelection,
  $isTextNode,
  COMMAND_PRIORITY_LOW,
  SELECTION_CHANGE_COMMAND,
} from "lexical";
import {
  NORMAL_SCRIPT_MODE,
  normalizeFontSize,
  normalizeRichTextModel,
} from "../../../../../../engine/modules/ui/richLabelModel.js";

const TEXT_SCRIPT_FORMATS = {
  sup: "superscript",
  sub: "subscript",
};

const FORMAT_TO_SCRIPT = {
  superscript: "sup",
  subscript: "sub",
};

const DEFAULT_STYLE_VALUES = {
  color: "#ffffff",
  backgroundColor: "rgba(33, 33, 33, 0.8)",
  fontSize: 90,
  lineSpacing: 20,
  borderRadius: 20,
  outlineWidth: 0,
  textAlign: "center",
};

const NUMERIC_RANGES = {
  fontSize: { min: 1 },
  lineSpacing: { min: 0, max: 50 },
  borderRadius: { min: 0, max: 50 },
};

const SDF_OUTLINE_STYLE_KEYS = ["outlineWidth", "sdfOutlineWidth", "strokeWidth", "sdfStrokeWidth"];

const ALIGNMENT_OPTIONS = [
  { value: "left", label: "Left", icon: "format_align_left" },
  { value: "center", label: "Center", icon: "format_align_center" },
  { value: "right", label: "Right", icon: "format_align_right" },
];

function parseInlineStyle(styleText = "") {
  return String(styleText)
    .split(";")
    .map((entry) => entry.trim())
    .filter(Boolean)
    .reduce((styles, entry) => {
      const separatorIndex = entry.indexOf(":");
      if (separatorIndex === -1) return styles;

      const property = entry.slice(0, separatorIndex).trim().toLowerCase();
      const value = entry.slice(separatorIndex + 1).trim();
      if (property && value) {
        styles[property] = value;
      }
      return styles;
    }, {});
}

function getTextNodeScript(textNode) {
  if (textNode.hasFormat(TEXT_SCRIPT_FORMATS.sup)) {
    return FORMAT_TO_SCRIPT.superscript;
  }

  if (textNode.hasFormat(TEXT_SCRIPT_FORMATS.sub)) {
    return FORMAT_TO_SCRIPT.subscript;
  }

  return NORMAL_SCRIPT_MODE;
}

function getTextNodeSegment(textNode) {
  const styles = parseInlineStyle(textNode.getStyle());
  const segment = {
    text: textNode.getTextContent(),
    script: getTextNodeScript(textNode),
  };

  if (styles.color) {
    segment.color = styles.color;
  }

  const fontSize = normalizeFontSize(styles["font-size"]);
  if (fontSize) {
    segment.fontSize = fontSize;
  }

  if (styles["font-family"]) {
    segment.fontFamily = styles["font-family"].replace(/^['"]|['"]$/g, "");
  }

  return segment;
}

function appendTextSegmentsFromNode(node, line) {
  if ($isTextNode(node)) {
    const segment = getTextNodeSegment(node);
    if (segment.text) {
      line.push(segment);
    }
    return;
  }

  if ($isElementNode(node)) {
    node.getChildren().forEach((child) => appendTextSegmentsFromNode(child, line));
  }
}

function getRichTextModelFromLexicalRoot() {
  const root = $getRoot();
  const lines = [];

  root.getChildren().forEach((node) => {
    const line = [];
    appendTextSegmentsFromNode(node, line);
    lines.push(line);
  });

  if (lines.length === 0) {
    lines.push([]);
  }

  return normalizeRichTextModel({ version: 1, lines });
}

function lexicalEditorStateToRichTextModel(editorState) {
  let model = normalizeRichTextModel({ version: 1, lines: [] });

  editorState.read(() => {
    model = getRichTextModelFromLexicalRoot();
  });

  return model;
}

function getSegmentStyle(segment = {}) {
  const styles = [];

  if (segment.color) {
    styles.push(`color: ${segment.color}`);
  }

  const fontSize = normalizeFontSize(segment.fontSize);
  if (fontSize) {
    styles.push(`font-size: ${fontSize}px`);
  }

  if (segment.fontFamily) {
    styles.push(`font-family: ${segment.fontFamily}`);
  }

  return styles.join("; ");
}

function appendModelLineToParagraph(paragraph, line = []) {
  if (line.length === 0) {
    return;
  }

  line.forEach((segment) => {
    const textNode = $createTextNode(segment.text);
    const scriptFormat = TEXT_SCRIPT_FORMATS[segment.script];

    if (scriptFormat) {
      textNode.toggleFormat(scriptFormat);
    }

    const style = getSegmentStyle(segment);
    if (style) {
      textNode.setStyle(style);
    }

    paragraph.append(textNode);
  });
}

function setLexicalEditorStateFromRichTextModel(editor, model) {
  const normalized = normalizeRichTextModel(model);

  editor.update(() => {
    const root = $getRoot();
    root.clear();

    const lines = normalized.lines.length > 0 ? normalized.lines : [[]];
    lines.forEach((line) => {
      const paragraph = $createParagraphNode();
      appendModelLineToParagraph(paragraph, line);
      root.append(paragraph);
    });
  }, { discrete: true });
}

function getSelectionScriptMode() {
  const selection = $getSelection();
  if (!$isRangeSelection(selection)) {
    return NORMAL_SCRIPT_MODE;
  }

  if (selection.hasFormat(TEXT_SCRIPT_FORMATS.sup)) {
    return "sup";
  }

  if (selection.hasFormat(TEXT_SCRIPT_FORMATS.sub)) {
    return "sub";
  }

  return NORMAL_SCRIPT_MODE;
}

function useEditorSelectionState() {
  const [editor] = useLexicalComposerContext();
  const [selectionState, setSelectionState] = useState({
    scriptMode: NORMAL_SCRIPT_MODE,
  });

  useEffect(() => {
    const updateSelectionState = () => {
      editor.getEditorState().read(() => {
        setSelectionState({
          scriptMode: getSelectionScriptMode(),
        });
      });
    };

    updateSelectionState();

    const removeUpdateListener = editor.registerUpdateListener(updateSelectionState);
    const removeSelectionCommand = editor.registerCommand(
      SELECTION_CHANGE_COMMAND,
      () => {
        updateSelectionState();
        return false;
      },
      COMMAND_PRIORITY_LOW
    );

    return () => {
      removeUpdateListener();
      removeSelectionCommand();
    };
  }, [editor]);

  return selectionState;
}

function InitialModelPlugin({ modelKey, model }) {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    setLexicalEditorStateFromRichTextModel(editor, model);
  }, [editor, modelKey]);

  return null;
}

function ToolbarButton({ active, label, title, onClick, children }) {
  return (
    <button
      type="button"
      className={`TextDrawerToolbarButton TextDrawerScriptButton${active ? " active" : ""}`}
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      title={title}
      aria-label={label}
      aria-pressed={active}
    >
      {children}
    </button>
  );
}

function ScriptIcon({ mode }) {
  return (
    <span className={`TextDrawerScriptIcon ${mode === "sup" ? "superscript" : "subscript"}`} aria-hidden="true">
      <span className="TextDrawerScriptIconBase">x</span>
      <span className="TextDrawerScriptIconScript">2</span>
    </span>
  );
}

function getToolbarStyleValue(style = {}, key) {
  if (key === "textAlign") {
    return style.textAlign || style.fontAlign || DEFAULT_STYLE_VALUES.textAlign;
  }

  if (Object.prototype.hasOwnProperty.call(style, key)) {
    return style[key];
  }

  return DEFAULT_STYLE_VALUES[key] ?? "";
}

function isPositiveStyleValue(value) {
  if (value === undefined || value === null || value === "") {
    return false;
  }

  const parsedValue = Number.parseFloat(value);
  return Number.isFinite(parsedValue) && parsedValue > 0;
}

function isSdfOutlineEnabled(style = {}) {
  const outlineStyleKey = SDF_OUTLINE_STYLE_KEYS.find((key) => Object.prototype.hasOwnProperty.call(style, key));
  return outlineStyleKey ? isPositiveStyleValue(style[outlineStyleKey]) : false;
}

function getRgbaValue(color) {
  return `rgba(${color.rgb.r},${color.rgb.g},${color.rgb.b},${color.rgb.a})`;
}

function ToolbarNumberControl({ item, icon, title, style, onStyleChange, compact }) {
  const range = NUMERIC_RANGES[item] || {};

  const handleCommit = (value) => {
    if (isNaN(value)) return;
    onStyleChange({ [item]: Number(value) });
  };

  return (
    <label className={`TextDrawerToolbarNumberControl${compact ? " compact" : ""}`} title={title}>
      <span className="material-icons TextDrawerToolbarInputIcon">{icon}</span>
      <CommitOnBlurNumberInput
        className="TextDrawerToolbarNumberInput"
        wrapperClassName="TextDrawerToolbarNumberInputWrap"
        min={range.min}
        max={range.max}
        value={getToolbarStyleValue(style, item)}
        onCommit={handleCommit}
        onClick={(e) => e.stopPropagation()}
        title={title}
      />
    </label>
  );
}

function ToolbarColorPicker({ color, styleKey, onClose, onStyleChange }) {
  return (
    <div
      className="ModelPropsInlineColorPanel TextDrawerToolbarColorPicker"
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      onTouchStart={(e) => e.stopPropagation()}
    >
      <div className="ModelPropsColorPickerWrap">
        <ChromePicker
          onChange={(nextColor) => {
            onStyleChange({ [styleKey]: getRgbaValue(nextColor) }, { refresh: false });
          }}
          onChangeComplete={(nextColor) => {
            onStyleChange({ [styleKey]: getRgbaValue(nextColor) });
          }}
          color={color}
        />
        <button type="button" className="StyleColorCloseButton" onClick={onClose}>
          <span className="material-icons">close</span>
          Close
        </button>
      </div>
    </div>
  );
}

function ToolbarColorButton({ styleKey, icon, title, style, activeColorPicker, setActiveColorPicker }) {
  const isOpen = activeColorPicker === styleKey;
  const color = getToolbarStyleValue(style, styleKey);

  return (
    <button
      type="button"
      className={`TextDrawerToolbarButton${isOpen ? " active" : ""}`}
      onClick={() => setActiveColorPicker(isOpen ? null : styleKey)}
      title={title}
      aria-label={title}
      aria-pressed={isOpen}
    >
      <span className="material-icons" style={{ color }}>{icon}</span>
    </button>
  );
}

function FontDropdownControl({ style, onStyleChange }) {
  const selectedFontValue = getSelectedFontValue(style.font);

  const handleFontChange = (font) => {
    onStyleChange({
      font,
      fontSize: getFontSizeFromStyle(style),
    });
  };

  return (
    <label className="TextDrawerToolbarSelectControl" title="Typography">
      <span className="material-icons TextDrawerToolbarInputIcon">font_download</span>
      <select
        className="TextDrawerToolbarFontSelect"
        aria-label="Typography"
        value={selectedFontValue}
        onChange={(e) => handleFontChange(e.target.value)}
        onClick={(e) => e.stopPropagation()}
      >
        {FONT_OPTIONS.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      <span className="material-icons TextDrawerToolbarSelectArrow" aria-hidden="true">arrow_drop_down</span>
    </label>
  );
}

function AlignmentDropdownControl({ style, onStyleChange }) {
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef(null);
  const currentAlign = getToolbarStyleValue(style, "textAlign");
  const currentOption = ALIGNMENT_OPTIONS.find((option) => option.value === currentAlign) || ALIGNMENT_OPTIONS[1];

  useEffect(() => {
    if (!open) return undefined;

    const handleClickAway = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickAway);
    return () => document.removeEventListener("mousedown", handleClickAway);
  }, [open]);

  const handleAlignmentChange = (alignment) => {
    onStyleChange({
      textAlign: alignment,
      fontAlign: alignment,
    });
    setOpen(false);
  };

  return (
    <div className="TextDrawerAlignmentDropdownControl" ref={dropdownRef}>
      <button
        type="button"
        className="TextDrawerToolbarButton"
        onClick={() => setOpen((current) => !current)}
        title={`Text alignment: ${currentOption.label}`}
        aria-label={`Text alignment: ${currentOption.label}`}
        aria-expanded={open}
      >
        <span className="material-icons">{currentOption.icon}</span>
      </button>
      {open ? (
        <div className="TextDrawerAlignmentDropdown">
          {ALIGNMENT_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              className={`TextDrawerToolbarButton${currentAlign === option.value ? " active" : ""}`}
              onClick={() => handleAlignmentChange(option.value)}
              title={`Align text ${option.label.toLowerCase()}`}
              aria-label={`Align text ${option.label.toLowerCase()}`}
              aria-pressed={currentAlign === option.value}
            >
              <span className="material-icons">{option.icon}</span>
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function RichTextToolbar({ style = {}, onStyleChange = () => {} }) {
  const [editor] = useLexicalComposerContext();
  const selectionState = useEditorSelectionState();
  const [toolbarStyle, setToolbarStyle] = useState(style || {});
  const [activeColorPicker, setActiveColorPicker] = useState(null);

  useEffect(() => {
    setToolbarStyle(style || {});
  }, [style]);

  const handleStyleChange = (styleUpdate, options) => {
    setToolbarStyle((currentStyle) => ({ ...currentStyle, ...styleUpdate }));
    onStyleChange(styleUpdate, options);
  };

  const toggleScriptMode = (nextMode) => {
    editor.update(() => {
      const selection = $getSelection();
      if (!$isRangeSelection(selection)) return;

      const format = TEXT_SCRIPT_FORMATS[nextMode];
      const otherFormat = nextMode === "sup" ? TEXT_SCRIPT_FORMATS.sub : TEXT_SCRIPT_FORMATS.sup;
      const shouldEnable = !selection.hasFormat(format);

      if (selection.hasFormat(otherFormat)) {
        selection.toggleFormat(otherFormat);
      }

      if (selection.hasFormat(format) !== shouldEnable) {
        selection.toggleFormat(format);
      }
    });
  };

  const toggleSdfOutline = () => {
    const nextOutlineWidth = isSdfOutlineEnabled(toolbarStyle) ? 0 : 1;
    handleStyleChange({ outlineWidth: nextOutlineWidth });
  };

  const activePickerColor = activeColorPicker
    ? getToolbarStyleValue(toolbarStyle, activeColorPicker)
    : null;
  const showSdfOutlineControl = toolbarStyle.textRenderer === "sdf";

  return (
    <div className="TextDrawerToolbar" onClick={(e) => e.stopPropagation()}>
      <div className="TextDrawerScriptButtons">
        <ToolbarButton
          active={selectionState.scriptMode === "sup"}
          label="Toggle superscript for future text"
          title="Toggle superscript for future text"
          onClick={() => toggleScriptMode("sup")}
        >
          <ScriptIcon mode="sup" />
        </ToolbarButton>
        <ToolbarButton
          active={selectionState.scriptMode === "sub"}
          label="Toggle subscript for future text"
          title="Toggle subscript for future text"
          onClick={() => toggleScriptMode("sub")}
        >
          <ScriptIcon mode="sub" />
        </ToolbarButton>
        {showSdfOutlineControl ? (
          <ToolbarButton
            active={isSdfOutlineEnabled(toolbarStyle)}
            label="Font bolding"
            title="Font bolding"
            onClick={toggleSdfOutline}
          >
            <span className="TextDrawerBoldIcon" aria-hidden="true">B</span>
          </ToolbarButton>
        ) : null}
      </div>

      <ToolbarColorButton
        styleKey="color"
        icon="format_color_text"
        title="Text colour"
        style={toolbarStyle}
        activeColorPicker={activeColorPicker}
        setActiveColorPicker={setActiveColorPicker}
      />
      <ToolbarColorButton
        styleKey="backgroundColor"
        icon="format_color_fill"
        title="Background colour"
        style={toolbarStyle}
        activeColorPicker={activeColorPicker}
        setActiveColorPicker={setActiveColorPicker}
      />
      <FontDropdownControl
        style={toolbarStyle}
        onStyleChange={handleStyleChange}
      />
      <ToolbarNumberControl
        item="fontSize"
        icon="format_size"
        title="Font size"
        style={toolbarStyle}
        onStyleChange={handleStyleChange}
        compact
      />
      <ToolbarNumberControl
        item="lineSpacing"
        icon="format_line_spacing"
        title="Line spacing"
        style={toolbarStyle}
        onStyleChange={handleStyleChange}
      />
      <ToolbarNumberControl
        item="borderRadius"
        icon="rounded_corner"
        title="Border radius"
        style={toolbarStyle}
        onStyleChange={handleStyleChange}
      />
      <AlignmentDropdownControl
        style={toolbarStyle}
        onStyleChange={handleStyleChange}
      />

      {activeColorPicker ? (
        <ToolbarColorPicker
          color={activePickerColor}
          styleKey={activeColorPicker}
          onClose={() => setActiveColorPicker(null)}
          onStyleChange={handleStyleChange}
        />
      ) : null}
    </div>
  );
}

function RichTextEditorChangePlugin({ onChange }) {
  return (
    <OnChangePlugin
      ignoreSelectionChange
      onChange={(editorState) => {
        onChange(lexicalEditorStateToRichTextModel(editorState));
      }}
    />
  );
}

function countModelLines(model) {
  return normalizeRichTextModel(model).lines.length;
}

function RichTextLexicalEditor({ model, modelKey, onChange, style, onStyleChange = () => {} }) {
  useEffect(() => {
    preloadMappedSdfFontsForText(model);
  }, [model]);

  const initialConfig = useMemo(() => ({
    namespace: "InventumLabelTextDrawer",
    onError(error) {
      throw error;
    },
    theme: {
      paragraph: "TextDrawerParagraph",
      text: {
        superscript: "TextDrawerTextRun--sup",
        subscript: "TextDrawerTextRun--sub",
      },
    },
  }), []);

  const lineCount = countModelLines(model);

  return (
    <LexicalComposer initialConfig={initialConfig}>
      <div className="TextDrawerEditorShell">
        <RichTextToolbar style={style} onStyleChange={onStyleChange} />
        <div className="TextDrawerEditorFrame">
          <RichTextPlugin
            contentEditable={(
              <ContentEditable
                className="TextDrawerEditorInput"
                aria-label="Label text"
                spellCheck={false}
              />
            )}
            placeholder={<div className="TextDrawerEditorPlaceholder">Type label text…</div>}
            ErrorBoundary={LexicalErrorBoundary}
          />
          <div className="TextDrawerCount">{lineCount} line{lineCount !== 1 ? "s" : ""}</div>
        </div>
        <HistoryPlugin />
        <InitialModelPlugin model={model} modelKey={modelKey} />
        <RichTextEditorChangePlugin onChange={onChange} />
      </div>
    </LexicalComposer>
  );
}

export {
  RichTextLexicalEditor,
  lexicalEditorStateToRichTextModel,
  setLexicalEditorStateFromRichTextModel,
};
